import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Nothing } from 'components/Nothing';
import moment from 'moment';
import { FC, useState } from 'react';
import {
  useGetOrderTemplatesQuery,
  useGetUnitMeasuresQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import { ReactComponent as IconForward } from './img/iconBackForward.svg';
import styles from './OrderTemplates.module.scss';
import { CreateOrderModal } from '../CreateOrderModal';
import { formatEndDate } from 'utils/date';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import { useMediaQuery } from '@mantine/hooks';
import { Template } from 'redux/Api/ordersApi/type';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Loader } from '@mantine/core';

export interface InitialTemplateType {
  name?: string;
  order_type?: string;
  order_type_id?: number;
  amount?: number;
  unit_price?: number | string | null;
  total_price?: number | string | null;
  is_unit_based?: boolean;
  unit_volume?: number | string | null;
  unit_volume_min?: number | string | null;
  unit_volume_max?: number | string | null;
  unit_measure?: string;
  is_remote?: boolean;
  accept_automatically?: boolean;
  city?: string | null;
  street?: string | null;
  home?: string | null;
  apartment?: string | null;
  description?: string;
  add_tax_amount?: boolean;
  created_at?: string;
  date_start?: Date | null;
  date_end?: Date | null;
  use_fast_withdraw?: boolean;
  template_name?: string;
}

interface Props {
  openNav: boolean | undefined;
  onSuccess?: (message?: string) => void;
}

export const OrderTemplates: FC<Props> = ({ openNav, onSuccess }) => {
  const [templatesParams, setTemplatesParams] = useState({ page: 1 });
  const {
    data: templates,
    isLoading,
    isFetching,
  } = useGetOrderTemplatesQuery(templatesParams);
  const { data: unitMeasures } = useGetUnitMeasuresQuery();
  const hasNextPage = templates?.meta
    ? templates.meta.last_page !== templates.meta.current_page
    : false;

  const showFullPageSkeleton = isLoading || (isFetching && templatesParams.page === 1);

  const [intersectionRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setTemplatesParams((prev) => ({ ...prev, page: prev.page + 1 })),
    rootMargin: '0px 0px 160px 0px',
  });

  const isLessThan1595px = useMediaQuery('(width < 1595px)');
  const isLessThan1390px = useMediaQuery('(width < 1400px)');

  const [open, setOpen] = useState(false);
  const [initialData, setInitialData] = useState<InitialTemplateType | null>(null);
  const [currentId, setCurrentId] = useState<number | null>(null);

  const showFullTable = !((!openNav && isLessThan1595px) || isLessThan1390px);

  if (!!showFullPageSkeleton) {
    return (
      <div className={styles.progressBlock}>
        <LoadingSkeleton fullWidth={true} />
      </div>
    );
  }

  const handleOpen = (item: Template) => {
    setOpen(true);

    const initialData: InitialTemplateType = {
      template_name: item.name,
      order_type_id: item.order_type_id,
      order_type: item.order_type,
      unit_price: item.unit_price,
      unit_volume: item.unit_volume,
      unit_volume_min: item.unit_volume_min,
      unit_volume_max: item.unit_volume_max,
      unit_measure: item.unit_measure,
      is_unit_based: item.is_unit_based,
      accept_automatically: item.accept_automatically,
      add_tax_amount: item.add_tax_amount,
      date_start: item.date_start ? moment(item.date_start).toDate() : null,
      date_end: item.date_end ? moment(item.date_end).toDate() : null,
      description: item.description || '',
      is_remote: item.is_remote,
      city: item.city,
      street: item.street,
      home: item.home,
      apartment: item.apartment,
      use_fast_withdraw: item.use_fast_withdraw,
    };

    setInitialData(initialData);
    setCurrentId(Number(item.id));
  };

  return (
    <div>
      {showFullTable ? (
        <div className={styles.table}>
          <div className={styles.table_title}>
            <p>Создан</p>
            <p>Название</p>
            <p>Категория</p>
            <p>Описание</p>
            <p>Период</p>
            <p style={{ textAlign: 'end' }}>Сумма</p>
          </div>
          {templates?.data?.length ? (
            templates.data.map((item) => (
              <div
                className={styles.table_row}
                key={item.id}
                onClick={() => handleOpen(item)}>
                <TextWithTooltip
                  content={moment(item.created_at).format('DD MMMM').replace('.', '')}
                />
                <TextWithTooltip content={item.name} />
                <TextWithTooltip content={item.order_type} />
                <TextWithTooltip
                  content={item.description ? item.description : 'Не указано'}
                />
                <TextWithTooltip
                  content={
                    item.date_end && item.date_start
                      ? `${formatEndDate(item.date_start)} – ${formatEndDate(
                          item.date_end,
                        )}`
                      : item.date_end
                      ? moment(item.date_end).format('DD MMMM').replace('.', '')
                      : 'Не указан'
                  }
                />
                <div style={{ textAlign: 'end' }}>
                  <TextWithTooltip
                    content={
                      item.unit_price
                        ? `${addSpaceNumber(item.unit_price)} ₽/${unitMeasures?.[
                            item.unit_measure as keyof typeof unitMeasures
                          ]}`
                        : 'Не указано'
                    }
                  />
                </div>
              </div>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Ничего не найдено' />
          )}
          {hasNextPage ? <div style={{ height: '10px' }} ref={intersectionRef} /> : null}
        </div>
      ) : (
        <div>
          {templates?.data?.length ? (
            templates.data.map((item) => (
              <div
                key={item.id}
                className={styles.mobile_row}
                onClick={() => handleOpen(item)}>
                <TextWithTooltip
                  content={item.name}
                  className={styles.mobile_row_name}
                  isMobile={true}
                />
                <div className={styles.icon}>
                  <IconForward />
                </div>
              </div>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Ничего не найдено' />
          )}
          {hasNextPage ? <div style={{ height: '10px' }} ref={intersectionRef} /> : null}
        </div>
      )}
      {!isLoading &&
        isFetching &&
        templatesParams.page > 1 &&
        (templates?.data?.length ?? 0) > 0 && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Loader size='xs' color='#002cfb' />
          </div>
        )}

      {open && (
        <CreateOrderModal
          setOpen={setOpen}
          initialData={initialData}
          currentId={currentId}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};
